type LogLevel = 'info' | 'warn' | 'error';
type LogData = Record<string, unknown>;

export type LogColor =
    | 'purple'
    | 'blue'
    | 'green'
    | 'orange'
    | 'teal'
    | 'pink'
    | 'red'
    | 'yellow'
    | 'lime'
    | 'brown'
    | 'slate'
    | 'black';

const LOG_COLORS: Record<LogColor, string> = {
    purple: 'rgb(139, 92, 246)',
    blue: 'rgb(59, 130, 246)',
    green: 'rgb(34, 197, 94)',
    orange: 'rgb(249, 115, 22)',
    teal: 'rgb(20, 184, 166)',
    pink: 'rgb(236, 72, 153)',
    red: 'rgb(239, 68, 68)',
    yellow: 'rgb(234, 179, 8)',
    lime: 'rgb(132, 204, 22)',
    brown: 'rgb(120, 53, 15)',
    slate: 'rgb(71, 85, 105)',
    black: 'rgb(23, 23, 23)',
};

function getColorStyle(color: LogColor): string {
    return `background-color: ${LOG_COLORS[color]}; color: white; padding: 4px; border-radius: 4px; font-weight: bold;`;
}

export interface Logger {
    info: (message: string, data?: LogData) => void;
    warn: (message: string, data?: LogData) => void;
    error: (message: string, data?: LogData) => void;
}

export interface LoggerOptions {
    environment: string;
    color?: LogColor;
    prefix?: string;
}

function shouldLog(level: LogLevel, environment?: string): boolean {
    if (environment?.toLowerCase() === 'production') {
        return level === 'error';
    }

    return true;
}

function writeLog(
    level: LogLevel,
    message: string,
    data: LogData = {},
    environment?: string,
    color?: LogColor,
    prefix?: string,
): void {
    if (!shouldLog(level, environment)) {
        return;
    }

    if (color) {
        const style = getColorStyle(color);
        const label = prefix ? `[${prefix}] ${message}` : message;
        const extraArgs = Object.keys(data).length > 0 ? [data] : [];

        if (level === 'error') {
            console.error(`%c${label}`, style, ...extraArgs);
            return;
        }

        if (level === 'warn') {
            console.warn(`%c${label}`, style, ...extraArgs);
            return;
        }

        // biome-ignore lint/suspicious/noConsole: Logging utility wraps console by design
        console.log(`%c${label}`, style, ...extraArgs);
        return;
    }

    const payload = {
        level,
        message,
        timestamp: new Date().toISOString(),
        ...data,
    };

    const output = JSON.stringify(payload);

    if (level === 'error') {
        console.error(output);
        return;
    }

    if (level === 'warn') {
        console.warn(output);
        return;
    }

    // biome-ignore lint/suspicious/noConsole: Logging utility wraps console by design
    console.log(output);
}

export function createLogger(environmentOrOptions: string | LoggerOptions): Logger {
    const options: LoggerOptions =
        typeof environmentOrOptions === 'string' ? { environment: environmentOrOptions } : environmentOrOptions;

    const { environment, color, prefix } = options;

    return {
        info(message: string, data?: LogData): void {
            writeLog('info', message, data, environment, color, prefix);
        },
        warn(message: string, data?: LogData): void {
            writeLog('warn', message, data, environment, color, prefix);
        },
        error(message: string, data?: LogData): void {
            writeLog('error', message, data, environment, color, prefix);
        },
    };
}

export function logInfo(message: string, data?: LogData, environment?: string): void {
    writeLog('info', message, data, environment);
}

export function logWarn(message: string, data?: LogData, environment?: string): void {
    writeLog('warn', message, data, environment);
}

export function logError(message: string, data?: LogData, environment?: string): void {
    writeLog('error', message, data, environment);
}
